import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert
} from '@mui/material';
import { API_BASE_URL } from '../services/api';

const ROLES = [
  { value: 'front_office', label: 'Front Office Authorizer' },
  { value: 'back_office_verifier', label: 'Back Office Verifier' },
  { value: 'back_office_final', label: 'Back Office Final Authorizer' },
  { value: 'authorizer', label: 'Authorizer' },
];
const PAGE_OPTIONS = [
  // Operations / deal types
  { value: 'transaction', label: 'Transaction' },
  { value: 'fixed_income_gsec', label: 'Fixed Income GSec' },
  { value: 'fixed_income_tbill', label: 'Fixed Income T-Bill' },
  { value: 'money_market', label: 'Money Market' },
  { value: 'fixed_deposit', label: 'Fixed Deposit' },
  { value: 'buyback', label: 'Buyback' },
  { value: 'repo', label: 'Repo' },
  // Maturity Handling = Daily Maturity Cashflow (/maturity). Assignable to all
  // authorizer roles including plain Authorizer.
  { value: 'maturity', label: 'Maturity Handling' },
  // FO/BO maturity approval blotters (role-routed separately from Handling).
  { value: 'maturity_authorization', label: 'Maturity Authorization Blotter' },
  { value: 'daily-cashflow-blotter', label: 'Daily Cashflow Blotter' },
  { value: 'daily-deal-blotter', label: 'Daily Deal Blotter' },
  { value: 'daily-transaction-blotter', label: 'Daily Transaction Blotter' },
  // Reports (values match the report tab keys used across the app)
  { value: 'gsec_report', label: 'Report: T Bond' },
  { value: 'tbill_report', label: 'Report: T-Bill' },
  { value: 'gsec_summary_report', label: 'Report: T Bond Summary' },
  { value: 'mark-to-market-report', label: 'Report: Mark to Market' },
  { value: 'repo-report', label: 'Report: Repo / Reverse Repo' },
  { value: 'buyback-report', label: 'Report: Buyback' },
  { value: 'sell-transaction-report', label: 'Report: Sell Transaction' },
  { value: 'portfolio-report', label: 'Report: Portfolio' },
  { value: 'counterparty-report', label: 'Report: Counterparty Transaction' },
  { value: 'coupon-maturity-blotter', label: 'Report: Coupon Maturity Blotter' },
  // Masters (values match the master tab keys used across the app)
  { value: 'payment_master', label: 'Master: Payment' },
  { value: 'isin_master', label: 'Master: ISIN' },
  { value: 'counterparty_master', label: 'Master: Counterparty' },
  { value: 'portfolio_master', label: 'Master: Portfolio' },
  { value: 'broker_master', label: 'Master: Broker' },
  { value: 'strategy_master', label: 'Master: Strategy' },
  { value: 'investment_approver_master', label: 'Master: Investment Approver' },
  { value: 'issuer_master', label: 'Master: Issuer' },
  { value: 'settlement_account_master', label: 'Master: Settlement Account' },
  { value: 'holiday_calendar', label: 'Master: Holiday Calendar' },
  { value: 'fund_centre_master', label: 'Master: Fund Centre' },
];

export default function AdminAuthorizerPanel() {
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [role, setRole] = useState('front_office');
  const [perDealLimit, setPerDealLimit] = useState('');
  const [perDayLimit, setPerDayLimit] = useState('');
  const [allowedPages, setAllowedPages] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Fetch users and assignments from backend (mocked for now)
    fetch(`${API_BASE_URL}/users`)
      .then(r => r.json())
      .then(data => setUsers(Array.isArray(data) ? data : (data.users || [])));
    fetch(`${API_BASE_URL}/authorizers`)
      .then(r => r.json())
      .then(data => setAssignments(data.assignments || []));
  }, []);

  const handleAssign = async () => {
    if (!selectedUser || !role || !perDealLimit || !perDayLimit || allowedPages.length === 0) {
      setErrorMsg('Please fill all fields.');
      return;
    }

    const payload = {
      user_id: selectedUser,
      role,
      per_deal_limit: perDealLimit,
      per_day_limit: perDayLimit,
      allowed_pages: allowedPages
    };
    const res = await fetch(`${API_BASE_URL}/authorizers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      setSuccessMsg('Assignment saved!');
      setErrorMsg('');
      // Refresh assignments
      fetch(`${API_BASE_URL}/authorizers`)
        .then(r => r.json())
        .then(data => setAssignments(data.assignments || []));
    } else {
      setErrorMsg('Failed to save assignment.');
    }
  };

  return (
    <Container maxWidth="md" className="ui-page admin-page-shell" sx={{ py: 4 }}>
      <Card className="ui-card" sx={{ p: 3, border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <Typography variant="h4" gutterBottom>Admin Authorizer Management</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Configure authorization levels for different transaction types. Buyback deals follow a three-level verification workflow: 
          Front Office → Back Office → Back Office Final.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>Select User</em></MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              {ROLES.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Per Deal Limit"
              type="number"
              value={perDealLimit}
              onChange={e => setPerDealLimit(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Per Day Limit"
              type="number"
              value={perDayLimit}
              onChange={e => setPerDayLimit(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Select
              fullWidth
              multiple
              value={allowedPages}
              onChange={e => setAllowedPages(e.target.value)}
              renderValue={selected => selected.map(v => PAGE_OPTIONS.find(p => p.value === v)?.label).join(', ')}
            >
              {PAGE_OPTIONS.map(page => (
                <MenuItem key={page.value} value={page.value}>{page.label}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={12}>
            <Button variant="contained" onClick={handleAssign}>Assign Authorizer</Button>
          </Grid>
        </Grid>

        {successMsg && <Snackbar open autoHideDuration={3000} onClose={() => setSuccessMsg('')}><Alert severity="success">{successMsg}</Alert></Snackbar>}
        {errorMsg && <Snackbar open autoHideDuration={3000} onClose={() => setErrorMsg('')}><Alert severity="error">{errorMsg}</Alert></Snackbar>}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Current Authorizer Assignments</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Per Deal Limit</TableCell>
                  <TableCell>Per Day Limit</TableCell>
                  <TableCell>Allowed Pages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{users.find(u => u.id === a.user_id)?.username || a.user_id}</TableCell>
                    <TableCell>{ROLES.find(r => r.value === a.role)?.label || a.role}</TableCell>
                    <TableCell>{a.per_deal_limit}</TableCell>
                    <TableCell>{a.per_day_limit}</TableCell>
                    <TableCell>{(a.allowed_pages || []).map(p => PAGE_OPTIONS.find(opt => opt.value === p)?.label).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>
    </Container>
  );
}
